import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/shared/contexts/AuthContext';
import { ThemeProvider } from '@/shared/contexts/ThemeContext';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { Layout, ClientLayout } from '@/shared/components';
import { Permission } from '@/shared/types/permissions';

// Feature: Identity
import { LoginPage, UsersPage, ClientsPage, AccessesPage, PermissionsOverviewPage, ProtectedRoute } from '@/features/identity';

// Feature: Admin
import { MainDashboardPage, AutomationsDashboardPage, OperationsPage, CustomPaymentLinksPage, AIUsagePage } from '@/features/admin';

// Feature: Client
import { ClientDashboardPage } from '@/features/client';

// Feature: Billing
import {
  PaymentPage,
  CustomPaymentPage,
  PaymentSuccessPage,
  PaymentPendingPage,
  PaymentFailurePage,
  SchedulePage,
  SalesListPage,
  FinancialDashboard,
  CommissionsPage,
  ProfitAdvancesPage,
  OperationalCostsPage,
  ManageRecurringCostsPage,
  CashBoxPage,
  PartnerDebtPage,
  ReceiptsPage,
  EmployeePaymentsPage,
} from '@/features/billing';
import DREPage from '@/features/billing/pages/DREPage';

// Feature: Automation
import {
  AutomationsPage,
  AutomationDetailPage,
  ExecutionsPage,
  ExecutionDetailPage,
} from '@/features/automation';

// Feature: Client Onboarding (novo módulo)
import {
  ClientOnboardingListPage,
  ClientOnboardingDetailPage,
} from '@/features/client-onboarding';

// Feature: People Core (CLT)
import {
  DashboardPeoplePage,
  MetricsPeoplePage,
  PontoPage,
  TimesheetPage,
  AjustesPage,
  ProjetosPage,
  AuditLogsPage,
  EmployeesHCMPage,
  EmployeeProfilePage,
  HRDashboardPage,
  NewEmployeePage,
  EditEmployeePage,
} from '@/features/people-core';

// Feature: Mileage Simulator
import { MultiQuoteSimulatorPage, ProtectedSimulator } from '@/features/mileage-simulator';

// Feature: WhatsApp Groups
import GroupsListPage from '@/features/whatsapp-groups/pages/GroupsListPage';
import GroupDetailPage from '@/features/whatsapp-groups/pages/GroupDetailPage';

// Feature: Promotion Groups
import { PromotionGroupsPage } from '@/features/promotion-groups';

// Feature: Suggestions
import SuggestionsPage from '@/features/suggestions/pages/SuggestionsPage';

// Feature: Incident Management (Central de Problemas)
import {
  IncidentsListPage,
  StatisticsPage,
  IncidentDetailPage,
  CreateIncidentPage,
  DocumentSignaturePage,
  PaymentAgreementsPage,
  ResetRequestsPage,
  CategoriesPage,
  IncidentSettingsPage,
  PendingApprovalsPage,
} from '@/features/incident-management/pages';

// Feature: Instagram
import PostXPage from '@/features/instagram/pages/PostXPage';
import ArticlesPage from '@/features/blog/pages/ArticlesPage';
import ArticleDetailPage from '@/features/blog/pages/ArticleDetailPage';

// Feature: Feedback e PDI
import {
  FeedbackPDIDashboardPage,
  CheckInsPage,
  PDIPage,
  MicroFeedbacksPage,
  PreparationFormPage,
} from '@/features/feedback-pdi';

// Feature: Reminders
import { RemindersListPage } from '@/features/reminders/pages/RemindersListPage';

// Feature: Training
import { CommunicationTrainingPage } from '@/features/training/pages/CommunicationTrainingPage';
import { TorreTrainingPage } from '@/features/training/pages/TorreTrainingPage';

// Feature: Operations
import OperationalDashboardPage from '@/features/operations/pages/OperationalDashboardPage';

// Feature: Demands (métricas de tempo de demandas do Notion)
import { DemandsPage, DemandDetailPage } from '@/features/demands';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos - alinhado com cache do backend
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 10 * 60 * 1000, // 10 minutos - garbage collection
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/payment/custom/:token" element={<CustomPaymentPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/pending" element={<PaymentPendingPage />} />
                <Route path="/payment/failure" element={<PaymentFailurePage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route
                  path="/mileage-simulator"
                  element={
                    <ProtectedSimulator>
                      <MultiQuoteSimulatorPage />
                    </ProtectedSimulator>
                  }
                />

                {/* Protected routes - Admin/Gestor ONLY */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'GESTOR']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<MainDashboardPage />} />
                  <Route path="automations/dashboard" element={<AutomationsDashboardPage />} />
                  <Route path="automations" element={<AutomationsPage />} />
                  <Route path="automations/:id" element={<AutomationDetailPage />} />
                  <Route path="executions" element={<ExecutionsPage />} />
                  <Route path="executions/:id" element={<ExecutionDetailPage />} />

                  {/* Protected routes - AI Usage (ADMIN ONLY) */}
                  <Route
                    path="ai-usage"
                    element={
                      <ProtectedRoute allowedRoles={['ADMIN']}>
                        <AIUsagePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="financial/dashboard" element={<FinancialDashboard />} />
                  <Route path="financial/dre" element={<DREPage />} />
                  <Route path="financial/sales" element={<SalesListPage />} />
                  <Route path="financial/commissions" element={<CommissionsPage />} />
                  <Route path="financial/payments" element={<EmployeePaymentsPage />} />
                  <Route path="financial/profit-advances" element={<ProfitAdvancesPage />} />
                  <Route path="financial/operational-costs" element={<OperationalCostsPage />} />
                  <Route path="financial/manage-recurring" element={<ManageRecurringCostsPage />} />
                  <Route path="financial/cash-box" element={<CashBoxPage />} />
                  <Route path="financial/partner-debt" element={<PartnerDebtPage />} />
                  <Route path="financial/receipts" element={<ReceiptsPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="accesses" element={<AccessesPage />} />
                  <Route path="permissions" element={<PermissionsOverviewPage />} />
                  <Route path="suggestions" element={<SuggestionsPage />} />
                  <Route path="instagram/post-x" element={<PostXPage />} />
                  <Route path="blog/articles" element={<ArticlesPage />} />
                  <Route path="blog/articles/:id" element={<ArticleDetailPage />} />
                </Route>

                {/* Protected routes - Operações (requer CLIENT_OPERATIONS) */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}
                      requiredPermission={Permission.CLIENT_OPERATIONS}
                    >
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="operations" element={<OperationsPage />} />
                  <Route path="operations/dashboard" element={<OperationalDashboardPage />} />
                  <Route path="demands" element={<DemandsPage />} />
                  <Route path="demands/:id" element={<DemandDetailPage />} />
                  <Route path="custom-payment-links" element={<CustomPaymentLinksPage />} />
                  <Route path="onboarding" element={<ClientOnboardingListPage />} />
                  <Route path="onboarding/:id" element={<ClientOnboardingDetailPage />} />
                </Route>

                {/* Protected routes - WhatsApp (requer WHATSAPP_OPERATIONS) */}
                <Route
                  path="/whatsapp"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}
                      requiredPermission={Permission.WHATSAPP_OPERATIONS}
                    >
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="groups" element={<GroupsListPage />} />
                  <Route path="groups/:groupJid" element={<GroupDetailPage />} />
                </Route>

                {/* Protected routes - Promotion Groups (requer WHATSAPP_OPERATIONS) */}
                <Route
                  path="/promotion-groups"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}
                      requiredPermission={Permission.WHATSAPP_OPERATIONS}
                    >
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<PromotionGroupsPage />} />
                </Route>

                {/* Protected routes - Clientes (todos os usuários autenticados exceto CLIENT) */}
                <Route
                  path="/clients"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ClientsPage />} />
                </Route>

                {/* Protected routes - Lembretes (todos os usuários autenticados exceto CLIENT) */}
                <Route
                  path="/reminders"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<RemindersListPage />} />
                </Route>

                {/* Protected routes - Training (requer TRAINING) */}
                <Route
                  path="/training"
                  element={
                    <ProtectedRoute
                      allowedRoles={['ADMIN', 'GESTOR', 'COLLABORATOR']}
                      requiredPermission={Permission.TRAINING}
                    >
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="communication" element={<CommunicationTrainingPage />} />
                  <Route path="torre" element={<TorreTrainingPage />} />
                </Route>

                {/* Protected routes - Feedback e PDI (Colaboradores, Gestores e Admin) */}
                <Route
                  path="/feedback-pdi"
                  element={
                    <ProtectedRoute allowedRoles={['COLLABORATOR', 'GESTOR', 'ADMIN']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<FeedbackPDIDashboardPage />} />
                  <Route path="check-ins" element={<CheckInsPage />} />
                  <Route path="pdi" element={<PDIPage />} />
                  <Route path="feed" element={<MicroFeedbacksPage />} />
                  <Route path="preparation" element={<PreparationFormPage />} />
                </Route>

                {/* Protected routes - People Core (CLT) */}
                <Route
                  path="/people-core"
                  element={
                    <ProtectedRoute allowedRoles={['COLLABORATOR', 'GESTOR', 'ADMIN']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<DashboardPeoplePage />} />
                  <Route path="metricas" element={<MetricsPeoplePage />} />
                  <Route path="ponto" element={<PontoPage />} />
                  <Route path="timesheet" element={<TimesheetPage />} />
                  <Route path="ajustes" element={<AjustesPage />} />
                  <Route path="projetos" element={<ProjetosPage />} />
                  {/* Audit Logs - ADMIN ONLY */}
                  <Route
                    path="audit-logs"
                    element={
                      <ProtectedRoute allowedRoles={['ADMIN']}>
                        <AuditLogsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* HCM - Gestão de Capital Humano */}
                  <Route path="hcm/dashboard" element={<HRDashboardPage />} />
                  <Route path="hcm/employees" element={<EmployeesHCMPage />} />
                  <Route path="hcm/employees/new" element={<NewEmployeePage />} />
                  <Route path="hcm/employees/:id/edit" element={<EditEmployeePage />} />
                  <Route path="hcm/employees/:id" element={<EmployeeProfilePage />} />
                  <Route path="suggestions" element={<SuggestionsPage />} />
                </Route>

                {/* Protected routes - Incident Management (Central de Problemas) */}
                <Route
                  path="/incident-management"
                  element={
                    <ProtectedRoute allowedRoles={['GESTOR', 'ADMIN']}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<IncidentsListPage />} />
                  <Route path="statistics" element={<StatisticsPage />} />
                  <Route path="pending-approvals" element={<PendingApprovalsPage />} />
                  <Route path="settings" element={<IncidentSettingsPage />} />
                  <Route path="new" element={<CreateIncidentPage />} />
                  <Route path=":id" element={<IncidentDetailPage />} />
                  <Route path="documents/:id" element={<DocumentSignaturePage />} />
                  <Route path="agreements" element={<PaymentAgreementsPage />} />
                  <Route path="reset-requests" element={<ResetRequestsPage />} />
                  <Route path="categories" element={<CategoriesPage />} />
                </Route>

                {/* Protected routes - CLIENT ONLY */}
                <Route
                  path="/client"
                  element={
                    <ProtectedRoute allowedRoles={['CLIENT']}>
                      <ClientLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<ClientDashboardPage />} />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
